import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api";
import styles from "./WebsiteList.module.css";

const BUSINESS_OPTIONS = ["본사", "용역", "소독", "종합", "카페가산", "카페독산", "카페금나래", "카페그린"];
const EMPTY = { name: "", link: "", related_business: [], site_id: "", site_pw: "", content: "", kakao_shared: null };



function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s); if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function getMyName() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u?.name || u?.username || u?.full_name || "나";
  } catch {
    return "나";
  }
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function WebsiteList() {
  const myName = getMyName();
  const isMobile = useIsMobile();

  const [user, setUser] = useState(null);
  // ✅ 삭제 권한: position_rank 2 또는 3만
  const canDelete = [2, 3].includes(parseInt(user?.position_rank, 10));

  const [rows, setRows] = useState([]);



  const [editId, setEditId] = useState(null);   // 수정 중 id, 신규는 "new"
  const [draft, setDraft] = useState(EMPTY);
  const [bizOpen, setBizOpen] = useState(false); // 관련사업 패널 열림

  // 통합검색 + 자동완성
  const [query, setQuery] = useState("");
  const [acOpen, setAcOpen] = useState(false);
  const [acIdx, setAcIdx] = useState(-1);
  const acRef = useRef(null);

  // 이력 모달
  const [histFor, setHistFor] = useState(null);
  const [histList, setHistList] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const load = () => api.get("/websites/").then(r => setRows(r.data)).catch(() => { });
  useEffect(() => { load(); }, []);

  // ✅ 현재 로그인 사용자(권한용)
  useEffect(() => {
    api.get("/auth/me/").then(r => setUser(r.data)).catch(() => {});
  }, []);


  // 외부 클릭 시 자동완성 닫기
  useEffect(() => {
    const h = (e) => { if (acRef.current && !acRef.current.contains(e.target)) setAcOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // 연번(전체 순서 기준)
  const seqOf = useMemo(() => {
    const m = {}; rows.forEach((r, i) => { m[r.id] = i + 1; }); return m;
  }, [rows]);

  // 행 검색 텍스트 (모든 필드 통합)
  const haystack = (r) => [
    seqOf[r.id], r.name, (r.related_business || []).join(" "),
    r.site_id, r.site_pw, r.content, r.updated_by_name,
    fmtDate(r.updated_at), r.kakao_shared ? "예 공유 O" : "아니오 미공유 X",
  ].join(" ").toLowerCase();

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => q ? rows.filter(r => haystack(r).includes(q)) : rows,
    [rows, q, seqOf]
  );

  // 자동완성 후보 (웹사이트명 위주)
  const suggestions = useMemo(() => q ? filtered.slice(0, 8) : [], [filtered, q]);

  const onSearchKey = (e) => {
    if (!acOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setAcIdx(i => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAcIdx(i => (i <= 0 ? suggestions.length - 1 : i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const pick = suggestions[acIdx] || suggestions[0];
      if (pick) { setQuery(pick.name); setAcOpen(false); jumpTo(pick.id); }
    }
    else if (e.key === "Escape") { setAcOpen(false); }
  };

  const jumpTo = (id) => {
    const el = document.getElementById(`ws-row-${id}`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add(styles.flash); setTimeout(() => el.classList.remove(styles.flash), 1500); }
  };

  // ── 수정/추가 ──────────────────────────────
  const startNew = () => { setEditId("new"); setDraft(EMPTY); setBizOpen(false); };
  const startEdit = (r) => {
    setEditId(r.id);
    setDraft({
      name: r.name, link: r.link || "", related_business: [...(r.related_business || [])],
      site_id: r.site_id, site_pw: r.site_pw, content: r.content, kakao_shared: r.kakao_shared
    });
    setBizOpen(false);
  };
  const cancel = () => { setEditId(null); setDraft(EMPTY); setBizOpen(false); };

  const toggleBiz = (b) => setDraft(d => ({
    ...d,
    related_business: d.related_business.includes(b)
      ? d.related_business.filter(x => x !== b)
      : [...d.related_business, b],
  }));

  const save = async () => {
    if (!draft.name.trim()) { alert("웹사이트명을 입력하세요."); return; }
    if (draft.kakao_shared === null || draft.kakao_shared === undefined) {
      alert("카톡 공유 여부를 반드시 선택하세요."); return;
    }

    // ✅ "아니오" 선택 시 공유 안내 팝업 (확인 누르면 저장 진행)
    if (draft.kakao_shared === false) {
      alert("반드시 카카오톡 단톡방에 공유하세요.");
    }

    try {
      if (editId === "new") await api.post("/websites/", draft);
      else await api.put(`/websites/${editId}/`, draft);
      cancel(); load();
    } catch (err) { alert("저장 실패: " + (err?.response?.data ? JSON.stringify(err.response.data) : err.message)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`[${r.name}] 삭제할까요?`)) return;
    try { await api.delete(`/websites/${r.id}/`); load(); }
    catch { alert("삭제 실패"); }
  };

  // ── 이력 모달 ──────────────────────────────
  const openHistory = async (r) => {
    setHistFor(r); setHistLoading(true); setHistList([]);
    try { const res = await api.get(`/websites/${r.id}/history/`); setHistList(res.data); }
    catch { setHistList([]); }
    finally { setHistLoading(false); }
  };

  // ── 바로가기 링크 ──────────────────────
  const LinkCell = ({ url }) => {
    if (!url) return <span style={{ color: "#999" }}>없음</span>;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={styles.linkBtn}>링크</a>
    );
  };




  // ── 렌더: 관련사업 셀 ──────────────────────
  const BizChips = ({ list }) => (
    <div className={styles.chips}>
      {(list || []).map(b => <span key={b} className={styles.chip}>{b}</span>)}
    </div>
  );

  // 관련사업 편집기 (드롭다운으로 추가 + 칩으로 제거)
  const BizEditor = () => {
    const addBiz = (b) => {
      if (!b) return;
      setDraft(d => d.related_business.includes(b)
        ? d
        : { ...d, related_business: [...d.related_business, b] });
    };
    const removeBiz = (b) => setDraft(d => ({
      ...d, related_business: d.related_business.filter(x => x !== b),
    }));
    const remaining = BUSINESS_OPTIONS.filter(b => !draft.related_business.includes(b));
    return (
      <div className={styles.bizEditor}>
        <select className={styles.inp} value="" onChange={e => addBiz(e.target.value)}>
          <option value="">+ 사업 추가</option>
          {remaining.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {draft.related_business.length > 0 &&
          <div className={styles.chips}>
            {draft.related_business.map(b => (
              <span key={b} className={styles.chipEdit} onClick={() => removeBiz(b)} title="클릭하여 제거">
                {b} ✕
              </span>
            ))}
          </div>}
      </div>
    );
  };

  // ── 편집 폼 (카드 공통) ──────────────────────
  const EditFields = () => (
    <>
    <div className={styles.cardField}>
        <label>웹사이트명</label>
        <input className={styles.inp} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
      </div>
      <div className={styles.cardField}>
        <label>바로가기 (링크)</label>
        <input className={styles.inp} placeholder="주소를 직접 복사" value={draft.link} onChange={e => setDraft(d => ({ ...d, link: e.target.value }))} />


      </div>
      <div className={styles.cardField}>
        <label>관련사업</label>
        <BizEditor />
      </div>
      <div className={styles.cardField}>
        <label>ID</label>
        <input className={styles.inp} value={draft.site_id} onChange={e => setDraft(d => ({ ...d, site_id: e.target.value }))} />
      </div>
      <div className={styles.cardField}>
        <label>PW</label>
        <input className={styles.inp} value={draft.site_pw} onChange={e => setDraft(d => ({ ...d, site_pw: e.target.value }))} />
      </div>
      <div className={styles.cardField}>
        <label>내용</label>
        <input className={styles.inp} value={draft.content} onChange={e => setDraft(d => ({ ...d, content: e.target.value }))} />
      </div>
      <div className={styles.cardField}>
        <label>카톡공유</label>
        <select className={styles.inp}
          value={draft.kakao_shared === null ? "" : String(draft.kakao_shared)}
          onChange={e => setDraft(d => ({ ...d, kakao_shared: e.target.value === "" ? null : e.target.value === "true" }))}>
          <option value="">선택</option>
          <option value="true">예</option>
          <option value="false">아니오</option>
        </select>
      </div>
    </>
  );

  // ── 모바일 카드 한 장 ──────────────────────
  const renderCard = (r) => {
    const editing = editId === r.id;
    return (
      <div key={r.id} id={`ws-row-${r.id}`} className={`${styles.card} ${editing ? styles.cardEditing : ""}`}>
        <div className={styles.cardHead}>
          <span className={styles.cardSeq}>#{seqOf[r.id]}</span>
          {editing
            ? <div className={styles.cardActs}>
                <button className={styles.btnSave} onClick={save}>저장</button>
                <button className={styles.btnGhost} onClick={cancel}>취소</button>
              </div>

            : <div className={styles.cardActs}>
                <button className={styles.btnGhost} onClick={() => openHistory(r)}>이력</button>
                <button className={styles.btnGhost} onClick={() => startEdit(r)}>수정</button>
                {canDelete &&
                  <button className={styles.btnDel} onClick={() => remove(r)}>삭제</button>}
              </div>}


        </div>

        {editing
          ? <EditFields />
          : <div className={styles.cardBody}>
              <div className={styles.cardRow}><span className={styles.cardLabel}>웹사이트명</span><span>{r.name}</span></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>바로가기</span><LinkCell url={r.link} /></div>



              <div className={styles.cardRow}><span className={styles.cardLabel}>관련사업</span><BizChips list={r.related_business} /></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>ID</span><span>{r.site_id}</span></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>PW</span><span>{r.site_pw}</span></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>내용</span><span>{r.content}</span></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>변경일자</span><span>{fmtDate(r.updated_at)}</span></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>변경자</span><span>{r.updated_by_name}</span></div>
              <div className={styles.cardRow}><span className={styles.cardLabel}>카톡공유</span><span>{r.kakao_shared ? "예" : "아니오"}</span></div>
            </div>}
      </div>
    );
  };

  // ── 신규 추가 카드 ──────────────────────
  const renderNewCard = () => (
    <div className={`${styles.card} ${styles.cardNew}`}>
      <div className={styles.cardHead}>
        <span className={styles.cardSeq}>NEW</span>
        <div className={styles.cardActs}>
          <button className={styles.btnSave} onClick={save}>저장</button>
          <button className={styles.btnGhost} onClick={cancel}>취소</button>
        </div>
      </div>
      <EditFields />
      <div className={styles.cardRow}><span className={styles.cardLabel}>변경자</span><span>{myName}</span></div>
    </div>
  );



  const renderRow = (r) => {
    const editing = editId === r.id;
    return (

      <tr key={r.id} id={`ws-row-${r.id}`} className={editing ? styles.editRow : undefined}>
        <td className={styles.cSeq}>{seqOf[r.id]}</td>

        <td className={styles.cName}>
          {editing
            ? <input className={styles.inp} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
            : r.name}
        </td>

        <td className={styles.cLink}>
          {editing
            ? <input className={styles.inp} placeholder="주소를 직접 복사" value={draft.link} onChange={e => setDraft(d => ({ ...d, link: e.target.value }))} />



            : <LinkCell url={r.link} />}
        </td>

        <td className={styles.cBiz}>



          {editing
            ? <BizEditor />
            : <BizChips list={r.related_business} />}
        </td>



        <td className={styles.cId}>
          {editing
            ? <input className={styles.inp} value={draft.site_id} onChange={e => setDraft(d => ({ ...d, site_id: e.target.value }))} />
            : r.site_id}
        </td>

        <td className={styles.cPw}>
          {editing
            ? <input className={styles.inp} value={draft.site_pw} onChange={e => setDraft(d => ({ ...d, site_pw: e.target.value }))} />
            : r.site_pw}
        </td>

        <td className={styles.cContent}>
          {editing
            ? <input className={styles.inp} value={draft.content} onChange={e => setDraft(d => ({ ...d, content: e.target.value }))} />
            : <span title={r.content}>{r.content}</span>}
        </td>

        <td className={styles.cDate}>{fmtDate(r.updated_at)}</td>
        <td className={styles.cUser}>{r.updated_by_name}</td>

        <td className={styles.cKakao}>
          {editing
            ? <select className={styles.inp}
              value={draft.kakao_shared === null ? "" : String(draft.kakao_shared)}
              onChange={e => setDraft(d => ({ ...d, kakao_shared: e.target.value === "" ? null : e.target.value === "true" }))}>
              <option value="">선택</option>
              <option value="true">예</option>
              <option value="false">아니오</option>
            </select>
            : (r.kakao_shared ? "예" : "아니오")}
        </td>

        <td className={styles.cAct}>
          {editing
            ? <>
              <button className={styles.btnSave} onClick={save}>저장</button>
              <button className={styles.btnGhost} onClick={cancel}>취소</button>
            </>

            : <>
              <button className={styles.btnGhost} onClick={() => openHistory(r)}>이력</button>
              <button className={styles.btnGhost} onClick={() => startEdit(r)}>수정</button>
              {canDelete &&
                <button className={styles.btnDel} onClick={() => remove(r)}>삭제</button>}
            </>}


        </td>
      </tr>
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <h2 className={styles.title}>ID/PW</h2>
        <button className={styles.btnAdd} onClick={startNew}>+ 추가</button>
      </div>



      {/* 통합검색 + 자동완성 */}
      <div className={styles.searchBox} ref={acRef}>
        <div className={styles.searchRow}>
          <input
            className={styles.search}
            placeholder="통합검색 (연번/웹사이트명/관련사업/ID/PW/내용/변경일자/변경자/카톡)"
            value={query}
            onChange={e=>{ setQuery(e.target.value); setAcOpen(true); setAcIdx(-1); }}
            onFocus={()=>{ if(query) setAcOpen(true); }}
            onKeyDown={onSearchKey}
          />
          {query && <button className={styles.clear} onClick={()=>{setQuery("");setAcOpen(false);}}>×</button>}
          <button className={styles.btnSearch} onClick={()=>setAcOpen(false)}>검색</button>
        </div>
        {acOpen && suggestions.length > 0 &&


          <ul className={styles.ac}>
            {suggestions.map((s,i)=>(
              <li key={s.id}
                  className={i===acIdx ? styles.acActive : styles.acItem}
                  onMouseEnter={()=>setAcIdx(i)}
                  onMouseDown={(e)=>{ e.preventDefault(); setQuery(s.name); setAcOpen(false); jumpTo(s.id); }}>
                <b>{s.name}</b>
                <span className={styles.acSub}>
                  {(s.related_business||[]).join(", ")} · {s.site_id} · {s.updated_by_name}
                </span>
              </li>
            ))}
          </ul>}
      </div>

      
      {isMobile ? (
        <div className={styles.cardList}>
          {editId === "new" && renderNewCard()}
          {filtered.map(renderCard)}
          {filtered.length === 0 && editId !== "new" &&
            <div className={styles.empty}>검색 결과가 없습니다.</div>}
        </div>
      ) : (
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <colgroup>

            <col style={{width:"44px"}}/>   {/* 연번 */}
            <col style={{width:"150px"}}/>  {/* 웹사이트명 */}
            <col style={{width:"140px"}}/>  {/* 바로가기 */}



            <col style={{width:"160px"}}/>  {/* 관련사업 */}
            <col style={{width:"120px"}}/>  {/* ID */}
            <col style={{width:"120px"}}/>  {/* PW */}
            <col/>                          {/* 내용 (가변) */}
            <col style={{width:"130px"}}/>  {/* 변경일자 */}
            <col style={{width:"80px"}}/>   {/* 변경자 */}
            <col style={{width:"72px"}}/>   {/* 카톡 */}
            <col style={{width:"210px"}}/>  {/* 관리 */}
          </colgroup>
          <thead>
            <tr>
              <th>연번</th><th>웹사이트명</th><th>바로가기</th><th>관련사업</th><th>ID</th><th>PW</th>


              <th>내용</th><th>변경일자</th><th>변경자</th><th>카톡공유</th><th>관리</th>
            </tr>
          </thead>
          <tbody>
            {editId === "new" &&
              <tr className={styles.newRow}>
                <td className={styles.cSeq}>NEW</td>
                <td><input className={styles.inp} value={draft.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))}/></td>

                <td><input className={styles.inp} placeholder="주소를 직접 복사" value={draft.link} onChange={e=>setDraft(d=>({...d,link:e.target.value}))}/></td>

                <td>
                  <BizEditor />
                </td>



                <td><input className={styles.inp} value={draft.site_id} onChange={e=>setDraft(d=>({...d,site_id:e.target.value}))}/></td>
                <td><input className={styles.inp} value={draft.site_pw} onChange={e=>setDraft(d=>({...d,site_pw:e.target.value}))}/></td>
                <td><input className={styles.inp} value={draft.content} onChange={e=>setDraft(d=>({...d,content:e.target.value}))}/></td>



                <td className={styles.cDate}>자동</td>
                <td className={styles.cUser}>{myName}</td>



                <td>
                  <select className={styles.inp}
                    value={draft.kakao_shared === null ? "" : String(draft.kakao_shared)}
                    onChange={e=>setDraft(d=>({...d, kakao_shared: e.target.value==="" ? null : e.target.value==="true"}))}>
                    <option value="">선택</option><option value="true">예</option><option value="false">아니오</option>
                  </select>
                </td>
                <td className={styles.cAct}>
                  <button className={styles.btnSave} onClick={save}>저장</button>
                  <button className={styles.btnGhost} onClick={cancel}>취소</button>
                </td>
              </tr>}

            {filtered.map(renderRow)}
            {filtered.length === 0 && editId !== "new" &&
              <tr><td colSpan={11} className={styles.empty}>검색 결과가 없습니다.</td></tr>}



          </tbody>
        </table>
      </div>
      )}


      {/* 이력 모달 */}
      {histFor &&
        <div className={styles.modalBg} onClick={() => setHistFor(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <span>지난 이력 — {histFor.name}</span>
              <button className={styles.modalClose} onClick={() => setHistFor(null)}>×</button>
            </div>
            <div className={styles.modalBody}>
              {histLoading ? <p>불러오는 중…</p> :
                histList.length === 0 ? <p>변경 이력이 없습니다.</p> :
                  <table className={styles.histTable}>
                    <thead>
                      <tr><th>변경일자</th><th>변경자</th><th>웹사이트명</th><th>바로가기</th><th>관련사업</th><th>ID</th><th>PW</th><th>내용</th><th>카톡</th></tr>


                    </thead>
                    <tbody>
                      {histList.map(h => (
                        <tr key={h.id}>
                          <td>{fmtDate(h.changed_at)}</td>
                          <td>{h.changed_by_name}</td>
                          <td>{h.name}</td>
                          <td><LinkCell url={h.link} /></td>


                          <td>{(h.related_business || []).join(", ")}</td>
                          <td>{h.site_id}</td>
                          <td>{h.site_pw}</td>
                          <td title={h.content}>{h.content}</td>
                          <td>{h.kakao_shared ? "예" : "아니오"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
            </div>
          </div>
        </div>}
    </div>
  );
}